// The website's host for the Transaction Wizard runner (obj_type "U") — a
// wizard embedded in a smart page that a visitor of the published site (or the
// platform's site viewer) runs (docs/plans/wizard-smart-page-embed.md).
//
// The runner is the shared `WizardRunner`; this is the site half of its seam,
// the twin of the Run view's `wizardHost.tsx` with a different transport:
//
//   • models     → GET /website/{owner}/{project}/site-wizard/{obj}
//   • transport  → the site-txn doors (/execute, /execute-batch,
//                  /resolve-defaults, /lookup) with `wizard: via` in every
//                  body and the embed's page-bound token in X-Embed-Token, so
//                  the server authorizes each call against the WIZARD's grant
//                  for this visitor (a signed-in project user, or the reserved
//                  site-visitor of an anonymous call)
//   • uploads    → site-doc/upload with the same token (the base
//                  transaction's document columns)
//   • panels / printing → the platform's own (the same endpoints a V / D / R
//                  embed on this page uses), so a panel shows on the site
//                  exactly when that object would
//
// A 401 EMBED_TOKEN_EXPIRED re-fetches the page for a fresh token, and a
// refusal that means "sign in" is handed to the embed's sign-in card — both
// through the embed data layer's own `withSiteEmbedRetry`.

import { useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import type { DataAccess } from '@/pages/workbench/smartpage-preview/dataBinding'
import { ReportViewer, withFixedParams } from '@twasta/page-runtime/report'
import { ObjectDrawer, makeApprovalsRuntime } from '@twasta/page-runtime/txn/screen'
import type { TxnScreenRuntime, BatchSaveBody, BatchSaveResult } from '@twasta/page-runtime/txn/screen'
import type {
  LookupResponse, RunActionRequest, RunActionResponse, TxnRuntime, UploadResult,
} from '@twasta/page-runtime/txn'
import type { ListResponse, LoadResponse } from '@twasta/page-runtime/txn/contracts'
import type { TransactionModel } from '@twasta/page-runtime/txn/types'
import { WizardRunner } from '@twasta/page-runtime/wizard'
import type {
  WizardCallOutcome, WizardHost, WizardModel, WizardObjectSpec, WizardPrintRequest, WizardVia,
} from '@twasta/page-runtime/wizard/contract'
import {
  siteWizardBatch, siteWizardDefaults, siteWizardExecute, siteWizardLookup, siteWizardModel,
} from '@/api/website'
import { wizardObjectOf } from '@/api/wizards'
import { makeReportRuntimeAdapter } from '@/pages/designers/report/ReportLivePreviewPane'
import { makeEmbedUploader, signInHandledByHost, withSiteEmbedRetry } from '../embedDataApi'
import { WizardObjectView, printWizardReport } from './wizardHost'

/** The site DataAccess a wizard embed carries (its own page-bound token). */
export type SiteWizardAccess = DataAccess & { mode: 'site' }

/** The embed's CURRENT access — read at call time, so a token the page
 *  refreshed (or a new sign-in hand-off) reaches a run already under way
 *  without rebuilding the host, which would restart it. */
export type SiteWizardAccessRef = () => SiteWizardAccess

const NOT_ON_SITE = 'This is not available on a website.'

const noteStyle = {
  padding: '10px 12px', fontSize: 13, color: 'var(--pg-text-secondary, #64748b)',
} as const

/**
 * The site TxnScreenRuntime of one wizard run: every request through the
 * site-txn doors, naming the wizard, carrying the embed's token.
 */
export function makeSiteWizardScreenRuntime(
  access: SiteWizardAccessRef, via: WizardVia,
  opts: { openWizard?: TxnRuntime['openWizardAction']; onSignInHandled?: () => void } = {},
): TxnScreenRuntime {
  // The owner and project of one embed never change; its token may.
  const { userId, project } = access()
  const call = <T,>(fn: (token?: string) => Promise<{ data: T }>) =>
    withSiteEmbedRetry(access(), async (token) => (await fn(token)).data).catch((err) => {
      // The embed answers this refusal with its sign-in card; the runner's own
      // error toast beside it would read as a failure.
      if (signInHandledByHost(access(), err)) opts.onSignInHandled?.()
      throw err
    })

  const execute: TxnScreenRuntime['execute'] = (objName, action, payload) =>
    call((tok) => siteWizardExecute(userId, project, objName, action, payload || {}, via, tok))

  const txn: TxnRuntime = {
    lookup: (_p, objName, lookup, search, limit, o) => call<LookupResponse>((tok) =>
      siteWizardLookup(userId, project, objName, {
        lookup, search: search || '', limit: limit ?? 50,
        filter: o?.filter, form_values: o?.formValues, header_values: o?.headerValues,
        literal_values: o?.literalValues, lookup_sql: o?.lookupSql,
        lookup_sql_data_col: o?.lookupSqlDataCol, lookup_field: o?.lookupField,
        lookup_inherited_field: o?.lookupInheritedField,
      }, via, tok) as Promise<{ data: LookupResponse }>),
    // "Add a new master" from a picker is an authoring flow; a website visitor
    // never creates another object's records from inside a wizard step.
    lookupSource: () => Promise.reject(new Error(NOT_ON_SITE)),
    resolveDefaults: (_p, objName) => call((tok) =>
      siteWizardDefaults(userId, project, objName, via, tok)).then((r) => ({
      ok: r.ok !== false, values: r.values || {}, ...(r.protect ? { protect: r.protect } : {}),
    })),
    save: (_p, objName, header, txnId, o) => execute(objName, 'save', {
      header, ...(txnId ? { txn_id: txnId } : {}),
      ...(o?.ignoreWarnings?.length ? { ignore_warnings: o.ignoreWarnings } : {}),
    }) as ReturnType<TxnRuntime['save']>,
    // The published application is already deployed.
    deploy: () => Promise.reject(new Error(NOT_ON_SITE)),
    uploadDocument: async (file: File): Promise<UploadResult> =>
      makeEmbedUploader(access(), project)(file),
    runAction: (_p, objName, req: RunActionRequest) => execute(objName, 'run_action', {
      form_no: req.formNo,
      action_name: req.actionName,
      header: req.header,
      header_values: req.headerValues || {},
      ...(req.txnId ? { txn_id: req.txnId } : {}),
      ...(req.parentPk && Object.keys(req.parentPk).length > 0 ? { parent_pk: req.parentPk } : {}),
      ...(req.ignoreWarnings?.length ? { ignore_warnings: req.ignoreWarnings } : {}),
      ...(req.confirm ? { confirm: req.confirm } : {}),
      ...(req.rowIsNew !== undefined ? { row_is_new: req.rowIsNew } : {}),
      ...(req.lineCount !== undefined ? { line_count: req.lineCount } : {}),
      ...(req.selectedRows ? { selected_rows: req.selectedRows } : {}),
    }) as Promise<RunActionResponse>,
    makeReportAdapter: (reportObjName: string) => makeReportRuntimeAdapter(project, reportObjName),
    ...(opts.openWizard ? { openWizardAction: opts.openWizard } : {}),
    notify: {
      success: (m: string) => toast.success(m),
      error: (m: string) => toast.error(m),
      info: (m: string) => toast(m, { icon: 'ℹ️' }),
      warn: (m: string) => toast(m, { icon: '⚠️' }),
    },
  }

  return {
    txn,
    // A wizard reads its base records through the execute funnel, as on the
    // Run view: a GET has no body to name the wizard in.
    list: (objName, q) => execute(objName, 'list', {
      limit: q.limit, offset: q.offset,
      ...(q.explicitLimit ? { explicit_limit: true } : {}),
      ...(q.formNo ? { form_no: q.formNo } : {}),
      ...(q.parentPk && Object.keys(q.parentPk).length ? { parent_pk: q.parentPk } : {}),
      ...(q.filters?.length ? { filters: q.filters } : {}),
      ...(q.enforcedOverrides && Object.keys(q.enforcedOverrides).length
        ? { enforced_overrides: q.enforcedOverrides } : {}),
      ...(q.bypass ? { bypass_enforced: true }
        : q.filterSetId ? { enforced_filter_set_id: q.filterSetId } : {}),
    }) as Promise<ListResponse>,
    load: (objName, txnId) => execute(objName, 'load', { txn_id: txnId }) as Promise<LoadResponse>,
    saveBatch: (objName, body: BatchSaveBody) => call<BatchSaveResult>((tok) =>
      siteWizardBatch(userId, project, objName, body as Parameters<typeof siteWizardBatch>[3],
        via, tok) as Promise<{ data: BatchSaveResult }>),
    // Records are never deleted through a wizard (the server refuses it too).
    remove: () => Promise.reject(new Error('Records cannot be deleted through a wizard.')),
    execute,
    resolveDefaults: (objName) => txn.resolveDefaults(project, objName),
    recordHistory: () => Promise.reject(new Error(NOT_ON_SITE)),
    approvals: makeApprovalsRuntime(execute),
  }
}

export interface SiteWizardHostOptions {
  previewReport?: (req: WizardPrintRequest) => void
  openWizard?: TxnRuntime['openWizardAction']
  startWizard?: WizardHost['startWizard']
  close?: WizardHost['close']
}

/** The website's WizardHost for one embed. */
export function makeSiteWizardHost(access: SiteWizardAccessRef, opts: SiteWizardHostOptions = {}): WizardHost {
  const { project, userId } = access()
  const wizards = new Map<string, Promise<{ wizard: WizardModel; base: TransactionModel | null }>>()
  const bases = new Map<string, TransactionModel>()
  const runtimes = new Map<string, TxnScreenRuntime>()
  let host: WizardHost | null = null
  // Set once a refusal went to the embed's sign-in card: the run is being
  // replaced by the card, so its error toasts are noise from here on.
  let signInHandled = false

  const readWizard = (objName: string) => {
    let hit = wizards.get(objName)
    if (!hit) {
      hit = withSiteEmbedRetry(access(), async (token) =>
        (await siteWizardModel(userId, project, objName, token)).data)
        .then((data) => {
          const res = wizardObjectOf(data, objName)
          const baseName = res.wizard.base_object
          if (res.base && baseName) bases.set(baseName.toLowerCase(), res.base)
          if (res.user && host) host.user = res.user
          return res
        })
      // A failed read is not cached: the next attempt may succeed.
      hit.catch(() => wizards.delete(objName))
      wizards.set(objName, hit)
    }
    return hit
  }

  const notify: WizardHost['notify'] = (kind, message) => {
    if (kind === 'success') toast.success(message)
    else if (kind === 'error') { if (!signInHandled) toast.error(message) }
    else if (kind === 'warning') toast(message, { icon: '⚠️' })
    else toast(message, { icon: 'ℹ️' })
  }

  host = {
    project,
    loadWizard: async (objName) => (await readWizard(objName)).wizard,
    loadTxnModel: async (objName) => {
      const cached = bases.get(objName.toLowerCase())
      if (cached) return cached
      // A website reads transaction models only as a wizard's base.
      throw new Error(`"${objName}" is not part of this wizard.`)
    },
    screenRuntime: (via: WizardVia) => {
      const key = `${via.obj_name}\u0000${via.run_id}`
      let rt = runtimes.get(key)
      if (!rt) {
        rt = makeSiteWizardScreenRuntime(access, via, {
          openWizard: opts.openWizard, onSignInHandled: () => { signInHandled = true },
        })
        runtimes.set(key, rt)
      }
      return rt
    },
    renderObject: (spec: WizardObjectSpec) => (spec.objType === 'V' || spec.objType === 'D' || spec.objType === 'R'
      ? <WizardObjectView project={project} spec={spec} />
      // Another transaction's record card would be a read of an object the
      // page does not host.
      : <div style={noteStyle}>{spec.title || spec.objName}: shown in the application, not on a website.</div>),
    printReport: (req) => printWizardReport(project, req, {
      preview: opts.previewReport || (() => notify('info', NOT_ON_SITE)), notify,
    }),
    startWizard: opts.startWizard,
    close: opts.close,
    notify,
  }
  return host
}

export interface SiteWizardProps {
  access: SiteWizardAccess
  wizard: string
  inputs?: Record<string, unknown>
  /** The run ended (finish.then 'close', cancel). Absent → it starts over. */
  onClose?: (result: { outcome: WizardCallOutcome; txnId?: string | null }) => void
}

/**
 * `WizardRunner` on the website host, plus the report preview a print step
 * opens. A `handoff` or a handler='wizard' button inside the wizard swaps the
 * next wizard in place — a page has nowhere to navigate to.
 */
export function SiteWizard({ access, wizard, inputs, onClose }: SiteWizardProps) {
  type Current = { wizard: string; inputs?: Record<string, unknown>; key: number }
  const [current, setCurrent] = useState<Current>({ wizard, inputs, key: 0 })
  const [preview, setPreview] = useState<WizardPrintRequest | null>(null)
  const cb = useRef({ onClose })
  cb.current = { onClose }
  const accessRef = useRef(access)
  accessRef.current = access

  // One host per embed: rebuilding it would restart the run, so the access
  // (its token, its sign-in hand-off) is read through the ref at call time.
  const host = useMemo(() => makeSiteWizardHost(() => accessRef.current, {
    previewReport: (req) => setPreview(req),
    openWizard: (req) => setCurrent((c) => ({ wizard: req.objName, inputs: req.inputs, key: c.key + 1 })),
    startWizard: (next, nextInputs) =>
      setCurrent((c) => ({ wizard: next, inputs: nextInputs, key: c.key + 1 })),
    close: (result) => {
      const fn = cb.current.onClose
      if (fn) fn(result)
      else setCurrent((c) => ({ wizard: c.wizard, inputs: c.inputs, key: c.key + 1 }))
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [access.userId, access.project])

  const previewAdapter = useMemo(() => (preview
    ? withFixedParams(makeReportRuntimeAdapter(access.project, preview.objName), preview.params || {})
    : null), [access.project, preview])

  return (
    <div data-tw-wizard-host={current.wizard}
      style={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <WizardRunner
        key={`${current.wizard}:${current.key}`}
        host={host}
        wizard={current.wizard}
        inputs={current.inputs}
        presentation="drawer"
      />
      {preview && previewAdapter && (
        <ObjectDrawer title={preview.title || preview.objName} objType="R" onClose={() => setPreview(null)}>
          <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <ReportViewer project={access.project} objName={preview.objName} adapter={previewAdapter} />
          </div>
        </ObjectDrawer>
      )}
    </div>
  )
}
